import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { TOOLTIP_DISPLAY_DELAY, TOOLTIP_POSITION_CLASSNAME_REGEX, TOOLTIP_REMOVE_DELAY, TOOLTIP_SIZE_CLASSNAME_REGEX, ZINDEX } from "../../common/constants";
import Popup from "../Popup/Popup";
import { BOTTOM_CENTER_POSITION, BOTTOM_LEFT_POSITION, BOTTOM_RIGHT_POSITION, LEFT_POSITION, RIGHT_POSITION, TOOLTIP_CLASS_PREFIX_LENGTH, TOP_CENTER_POSITION, TOP_LEFT_POSITION, TOP_RIGHT_POSITION } from "./constants";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const Tooltip = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    trigger,
    label,
    disabled,
    zIndex,
    contentStyle,
    position,
    children,
    noAriaDescribedby
  } = _ref;
  const popupRef = useRef();
  const theme = useTheme();
  let size = null,
      setWidthToTrigger = null;

  if (className) {
    const classNames = className.split(" ");
    classNames.forEach(className => {
      if (TOOLTIP_SIZE_CLASSNAME_REGEX.test(className)) {
        const classSize = className.slice(TOOLTIP_CLASS_PREFIX_LENGTH);

        if (classSize === "full") {
          setWidthToTrigger = true;
        } else {
          size = classSize;
        }
      }

      if (TOOLTIP_POSITION_CLASSNAME_REGEX.test(className)) {
        const classPosition = className.slice(TOOLTIP_CLASS_PREFIX_LENGTH);

        if (classPosition === "top-left") {
          position = TOP_LEFT_POSITION;
        } else if (classPosition === "top-center") {
          position = TOP_CENTER_POSITION;
        } else if (classPosition === "top-right") {
          position = TOP_RIGHT_POSITION;
        } else if (classPosition === "bottom-left") {
          position = BOTTOM_LEFT_POSITION;
        } else if (classPosition === "bottom-center") {
          position = BOTTOM_CENTER_POSITION;
        } else if (classPosition === "bottom-right") {
          position = BOTTOM_RIGHT_POSITION;
        } else if (classPosition === "left") {
          position = LEFT_POSITION;
        } else {
          position = RIGHT_POSITION;
        }
      }
    });
  }

  const {
    cssRoot,
    cssLabel,
    cssArrow
  } = extractTheme({
    theme,
    size
  });

  const combinedContentStyle = _objectSpread(_objectSpread({}, cssRoot), contentStyle); // Allow calling of these methods in Popup through a Tooltip ref.


  useImperativeHandle(ref, () => ({
    open: () => popupRef.current.open(),
    close: () => popupRef.current.close(),
    toggle: () => popupRef.current.toggle()
  }));
  return disabled ? trigger : _jsx(Popup, {
    noAriaDescribedby: noAriaDescribedby,
    ref: popupRef,
    trigger: trigger,
    on: ["focus", "hover"],
    position: position,
    repositionOnResize: true,
    mouseEnterDelay: TOOLTIP_DISPLAY_DELAY,
    mouseLeaveDelay: TOOLTIP_REMOVE_DELAY,
    contentStyle: combinedContentStyle,
    arrowStyle: cssArrow,
    zIndex: zIndex,
    setWidthToTrigger: setWidthToTrigger,
    label: label,
    closeOnEscape: true,
    children: children ? children : _jsx("span", {
      css: cssLabel,
      children: label
    })
  });
});
Tooltip.propTypes = process.env.NODE_ENV !== "production" ? {
  label: PropTypes.string.isRequired,
  trigger: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  children: PropTypes.node,
  contentStyle: PropTypes.shape(),
  disabled: PropTypes.bool,
  // To prevent `aria-describedby` from being added by tooltip to trigger element.
  noAriaDescribedby: PropTypes.bool,
  position: PropTypes.oneOfType([PropTypes.oneOf(["top left", "top center", "top right", "right top", "right center", "right bottom", "bottom left", "bottom center", "bottom right", "left top", "left center", "left bottom"]), PropTypes.arrayOf(PropTypes.string)]),
  zIndex: PropTypes.number
} : {};
Tooltip.defaultProps = {
  children: null,
  contentStyle: {},
  disabled: false,
  position: ["top left", "top center", "bottom left", "bottom center", "left center", "right center"],
  zIndex: ZINDEX.TOOLTIP,
  noAriaDescribedby: false
};
Tooltip.displayName = "Tooltip";
export default Tooltip;